/**
 * Sync Ticket Break Rows
 * 
 * Ensures exactly one ticket per break per tech owns the break row.
 * Snaps start times that fall within breaks to the end of the break.
 * Resets break_buffer_remaining for tickets no longer spanning breaks.
 */

import { supabase } from '@/integrations/supabase/client';
import { parseBreaksForDate, ticketSpansBreak, getMinutesFromMidnight } from './breakBufferHelpers';
import { parseBreaksToMinutes, getMinutesInShopTZ, makeUTCFromShopMinutes } from './timezoneMath';
import { triggerBoardCascade } from './cascadeTrigger';
// CSI-I2-A3: route the start_time-bearing batched writes through the
// canonical schedule-mutation helper. break_buffer_remaining travels as
// a companion field. Companion-only resets (no allowlist field touched)
// remain as raw writes — the canonical helper rejects empty payloads
// (no scheduling field), and break_buffer_remaining is intentionally
// non-allowlist (it is operational metadata about how a break is being
// absorbed, not part of the scheduling truth surface).
import { mutateTicketSchedule } from './mutateTicketSchedule';
import { replanRolloverChainFromRow } from './rolloverChain';
import { laneChainGoverned } from '../../supabase/functions/_shared/rolloverChainAuthority';

interface SyncOptions {
  techId: string;
  dateStr: string;
}

/**
 * Sync break row ownership for a technician's schedule
 * 
 * 1. Load all tickets for tech on date, ordered by start_time
 * 2. For each break window:
 *    - Determine which ticket spans the break (start < break.end && opti_finish > break.start)
 *    - That ticket "owns" the break
 * 3. Ensure downstream tickets don't start inside breaks (snap to end if needed)
 * 4. Reset break_buffer_remaining for tickets no longer spanning breaks
 */
export async function syncTicketBreakRowsWithSchedule(options: SyncOptions): Promise<void> {
  const { techId, dateStr } = options;

  try {
    // Fetch technician info with breaks
    const { data: tech, error: techError } = await supabase
      .from('technicians')
      .select('id, shop_id, recurring_breaks, non_recurring_breaks')
      .eq('id', techId)
      .single();

    if (techError || !tech) {
      console.warn('[SyncBreakRows] Could not fetch technician:', techError);
      return;
    }

    // Fetch shop timezone
    const { data: shop } = await supabase
      .from('shop_profile')
      .select('time_zone')
      .eq('shop_id', tech.shop_id)
      .single();

    const timezone = shop?.time_zone || 'America/Los_Angeles';

    // Get all active tickets for this tech on this date
    const { data: tickets, error: ticketsError } = await supabase
      .from('tickets')
      .select('id, start_time, opti_finish, break_buffer_remaining, status, timer_status, actually_started, rollover_of_ticket_id, deleted_at')
      .eq('assigned_tech_id', techId)
      .eq('scheduled_date', dateStr)
      .or('status.eq.Active,and(status.eq.Paused,actually_started.eq.true,bay_id.not.is.null)')
      .order('start_time', { ascending: true });

    if (ticketsError || !tickets || tickets.length === 0) {
      return;
    }

    // Parse breaks for this date
    const breaks = parseBreaksForDate(
      tech.recurring_breaks as any,
      tech.non_recurring_breaks as any,
      dateStr
    );

    if (breaks.length === 0) {
      // No breaks - reset any break buffers.
      //
      // CSI-I2-A3 SAFE — companion-only write (break_buffer_remaining is
      // NOT in ALLOWED_SCHEDULE_FIELDS). The canonical schedule-mutation
      // helper rejects empty payloads (it requires at least one
      // allowlisted scheduling field), and there is no scheduling field
      // to land here — the no-breaks reset is purely a buffer accounting
      // wipe. Documented intentionally raw so a future grep that flags
      // raw writes can short-circuit on this comment.
      const ticketsWithBuffer = tickets.filter(t => t.break_buffer_remaining && t.break_buffer_remaining > 0);
      if (ticketsWithBuffer.length > 0) {
        await Promise.all(
          ticketsWithBuffer.map(t =>
            supabase.from('tickets').update({ break_buffer_remaining: null }).eq('id', t.id)
          )
        );
      }
      return;
    }

    const updates: Array<{ id: string; start_time?: string; break_buffer_remaining?: number | null }> = [];

    // ROLLOVER CHAIN ROWS (canonical identity) are never snapped here: moving
    // one row's start alone would leave its finish and the chain's later days
    // stale. A governed row that starts inside a break is re-planned — with
    // the chain's unresolved future — by the rollover chain authority from
    // the first minute after the break. A canonical plan never starts inside
    // a break, so the next sync finds nothing to do.
    const chainGoverned = await laneChainGoverned(supabase, tickets as never);
    const chainRequests: Array<{ id: string; min: number }> = [];

    // Process each ticket
    for (const ticket of tickets) {
      if (!ticket.start_time || !ticket.opti_finish) continue;

      const startMin = getMinutesInShopTZ(ticket.start_time, timezone);
      const optiFinishMin = getMinutesInShopTZ(ticket.opti_finish, timezone);

      // Check if start_time falls inside any break
      let adjustedStartMin = startMin;
      let startWasAdjusted = false;

      for (const brk of breaks) {
        if (adjustedStartMin >= brk.start && adjustedStartMin < brk.end) {
          // Snap to end of break
          adjustedStartMin = brk.end;
          startWasAdjusted = true;
        }
      }

      // Check if ticket still spans any break after potential adjustment
      const spannedBreak = ticketSpansBreak(adjustedStartMin, optiFinishMin, breaks);

      if (startWasAdjusted && chainGoverned.has(ticket.id)) {
        chainRequests.push({ id: ticket.id, min: adjustedStartMin });
      } else if (startWasAdjusted) {
        const newStartTime = makeUTCFromShopMinutes(dateStr, adjustedStartMin, timezone);
        updates.push({
          id: ticket.id,
          start_time: newStartTime.toISOString(),
          // If no longer spanning a break, clear buffer
          break_buffer_remaining: spannedBreak ? ticket.break_buffer_remaining : null,
        });
      } else if (!spannedBreak && ticket.break_buffer_remaining && ticket.break_buffer_remaining > 0) {
        // Ticket doesn't span break anymore, clear buffer
        updates.push({
          id: ticket.id,
          break_buffer_remaining: null,
        });
      }
    }

    // Apply updates.
    //
    // CSI-I2-A3: closes the prior commented-bypass note. start_time is
    // an allowlisted scheduling column and now routes through the
    // canonical `mutateTicketSchedule` helper. break_buffer_remaining
    // travels atomically as a companion field when paired with a
    // start_time snap. The narrow scope (same tech, same date,
    // break-alignment snap only — no tech/bay/date change) is preserved.
    //
    // Two update shapes flow through this map:
    //   (a) start_time present  → canonical helper, with optional
    //       break_buffer_remaining companion. Allowlist-gated.
    //   (b) start_time absent   → companion-only buffer wipe (mirror of
    //       the no-breaks branch above). Stays raw because the canonical
    //       helper rejects empty schedule-field payloads and
    //       break_buffer_remaining is intentionally non-allowlist.
    //
    // skipCascade:true preserves the SINGLE post-loop cascade trigger
    // below — without it, each routed update would fire its own
    // cascade scoped to this same (shop, date, tech) tuple, multiplying
    // cascade calls by `updates.length`.
    if (updates.length > 0) {
      await Promise.all(
        updates.map((update) => {
          if (update.start_time !== undefined) {
            const companionFields: Record<string, unknown> = {};
            if (update.break_buffer_remaining !== undefined) {
              companionFields.break_buffer_remaining = update.break_buffer_remaining;
            }
            return mutateTicketSchedule(
              update.id,
              { start_time: update.start_time },
              {
                reason: 'sync_break_rows_start_time_snap',
                skipCascade: true,
                ...(Object.keys(companionFields).length > 0
                  ? { companionFields }
                  : {}),
              },
            );
          }
          // CSI-I2-A3 SAFE — companion-only buffer wipe (no allowlist
          // field touched). See doctrinal note on the no-breaks branch
          // above; same rationale applies here.
          return supabase
            .from('tickets')
            .update({ break_buffer_remaining: update.break_buffer_remaining })
            .eq('id', update.id);
        }),
      );

      console.log(`[SyncBreakRows] Updated ${updates.length} tickets for tech ${techId} on ${dateStr}`);

      // Trigger cascade so downstream tickets reflow after break-alignment snaps
      await triggerBoardCascade(tech.shop_id, dateStr, techId);
    }

    for (const r of chainRequests) {
      await replanRolloverChainFromRow(r.id, { date: dateStr, min: r.min }, { label: 'sync_break_rows' });
    }
  } catch (error) {
    console.error('[SyncBreakRows] Error syncing break rows:', error);
  }
}
